/**
 * Edit-report panel for reports.
 */
import * as React from 'react';
import { Star, FileText } from 'lucide-react';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { useGetReport, usePatchReport } from '@/hooks/useReportsRemote';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { REPORT_REMOTE_SCHEMA } from '@/components/reports/report-configs';
import type { ReportDetail } from '@/types/remote/report.types';
import { ReportTab } from '@/components/reports/remote/report-form-builder/ReportTab';
import { ScoringTab } from '@/components/reports/remote/report-form-builder/ScoringTab';
import {
  applyReportFieldPatch,
  initReportDraftFields,
  isReportReadOnly,
} from '@/components/reports/remote/report-form-utils';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link EditReportView}. */
interface EditReportViewProps {
  reportId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit panel for a remote report. */
export const EditReportView: React.FC<Readonly<EditReportViewProps>> = ({
  reportId,
  onClose,
  onSuccess,
}) => {
  const { data, isLoading, isError, error } = useGetReport(reportId);
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'reportNotFound',
    onClose,
    entityId: reportId,
  });
  const patchMutation = usePatchReport();
  const [activeTab, setActiveTab] = React.useState<'report' | 'scoring'>('report');
  const [draftFields, setDraftFields] = React.useState<Record<string, unknown>>({});
  const [initialised, setInitialised] = React.useState(false);

  const tabs: TabItem<'report' | 'scoring'>[] = [
    { id: 'report', label: 'Compte rendu', icon: <FileText className="w-3 h-3" /> },
    { id: 'scoring', label: 'Scoring', icon: <Star className="w-3 h-3" /> },
  ];

  React.useEffect(() => {
    if (!data || initialised) return;
    if (data.reportView?.fields) {
      setDraftFields(initReportDraftFields(data.reportView.fields as Record<string, unknown>));
    }
    setInitialised(true);
  }, [data, initialised]);

  const applyPatch = (patch: Record<string, unknown>) =>
    setDraftFields((prev) => applyReportFieldPatch(prev, patch));

  if (isLoading) {
    return (
      <div className="form-panel-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const reportView = data?.reportView;
  const scoringView = data?.scoringView;

  if (error || !data || !reportView) {
    return (
      <div className="p-6 text-center text-red-500">{getErrorMessage('loadReportFailed')}</div>
    );
  }

  const { permissions } = data as ReportDetail;
  const schemaId = reportView.schemaId;
  const isReadOnly = isReportReadOnly(permissions, schemaId, reportView.status);

  let editTitle = 'Modifier le compte rendu';
  let editSubtitle = 'Modifiez les informations du compte rendu';
  if (schemaId === REPORT_REMOTE_SCHEMA.tutoring) {
    editTitle = 'Modifier le compte rendu de tutorat individuel';
    editSubtitle = 'Modifiez les informations du compte rendu de tutorat individuel';
  } else if (schemaId === REPORT_REMOTE_SCHEMA.synthesisInformationMeeting) {
    editTitle = "Modifier la synthèse de réunion d'information";
    editSubtitle = "Modifiez les informations de la synthèse de réunion d'information";
  } else if (schemaId === REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting) {
    editTitle = "Modifier la synthèse de réunion d'accueil";
    editSubtitle = "Modifiez les informations de la synthèse de réunion d'accueil";
  }
  if (isReadOnly) {
    editSubtitle = getErrorMessage('reportReadOnly');
  }

  /** Validate, PATCH the report, then close. */
  const handleSubmit = async () => {
    try {
      await patchMutation.mutateAsync({ id: reportId, fields: draftFields });
      toast.success('Compte rendu enregistré avec succès !');
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <CollapseReplaceForm
      formType="edit"
      title={editTitle}
      subtitle={editSubtitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={patchMutation.isPending}
      isValid={!isReadOnly && activeTab === 'report'}
      isLoading={!initialised}
    >
      <Tabs
        items={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as 'report' | 'scoring')}
        className="mb-4"
      />

      {activeTab === 'report' && (
        <ReportTab
          schemaId={schemaId}
          status={reportView.status}
          fields={draftFields}
          onChange={applyPatch}
          isReadOnly={isReadOnly}
        />
      )}

      {activeTab === 'scoring' && (
        <ScoringTab schemaId={scoringView?.schemaId ?? ''} entries={scoringView?.entries} />
      )}
    </CollapseReplaceForm>
  );
};
